import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FAIcon } from './utils/FAIcons';
import {
	autoUpdate,
	FloatingPortal,
	useHover,
	useFocus,
	useDismiss,
	useRole,
	useInteractions,
	useFloating,
	flip,
	shift,
	offset,
} from '@floating-ui/react';

type Props = {
	children: React.ReactNode;
	content: string | React.ReactNode;
	position?: 'top' | 'bottom' | 'left' | 'right';
	colors?: string;
	className?: string;
	disabled?: boolean;
	/** Classes for the element wrapping `children`, e.g. `w-full self-stretch` for a trigger that should fill its container. */
	referenceClassName?: string;
};

export function Tooltip({ children, content, position = 'top', colors = 'text-white bg-black', className, disabled, referenceClassName }: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const { refs, floatingStyles, context } = useFloating({
		middleware: [offset(4), flip(), shift({ padding: 8 })],
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: position,
		whileElementsMounted: autoUpdate,
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([
		useHover(context, { move: false }),
		useFocus(context),
		useDismiss(context),
		useRole(context, {
			role: 'tooltip',
		}),
	]);

	return (
		<div className="relative flex items-center">
			<div className={clsx('cursor-pointer', referenceClassName)} ref={refs.setReference} {...getReferenceProps()}>
				{children}
			</div>
			{!disabled && isOpen && (
				<FloatingPortal>
					<div
						// Portaled to <body>, so a high z-index keeps the tooltip above modals (z-1000) and other overlays.
						className={`z-[10000] p-2 min-w-[240px] text-sm ${colors} rounded-md ${className}`}
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
					>
						{content}
					</div>
				</FloatingPortal>
			)}
		</div>
	);
}

export function LightTooltip(props: Omit<Props, 'colors' | 'className'>) {
	return <Tooltip {...props} className="shadow-md border border-primary" colors="bg-white text-gray-700" />;
}

// `className` is appended to the floating panel's classes — pass a max-width (e.g. `max-w-sm`) for
// longer content, since Tooltip only sets a min-width and would otherwise render one very wide line.
export function MenuInfoTooltip({ className, content }: { className?: string; content: ReactNode }) {
	return (
		<Tooltip className={clsx('shadow-md border border-primary', className)} colors="bg-white text-gray-700" content={content}>
			<FAIcon icon="circle-info" className="ml-1 text-primary text-sm" />
		</Tooltip>
	);
}

type TableTooltipProps = {
	children: React.ReactNode;
	position?: 'top' | 'bottom' | 'left' | 'right';
	title: string;
	icon: string;
	visibleCount?: number;
	values: {
		id: number;
		label: string;
	}[];
};

export function TableTooltip({ children, position = 'top', icon, title, values, visibleCount = 10 }: TableTooltipProps) {
	const [isVisible, setIsVisible] = useState(false);

	let positionClasses: string;
	switch (position) {
		case 'bottom':
			positionClasses = 'bottom-full mb-1';
			break;
		case 'left':
			positionClasses = 'left-full ml-1';
			break;
		case 'right':
			positionClasses = 'right-full mr-1';
			break;
		default:
			positionClasses = 'top-full mt-1';
	}

	const visibleValues = values.slice(0, visibleCount);
	const extraValues = values.length - visibleCount;

	return (
		<div className="relative flex items-center">
			<div onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} className="cursor-pointer">
				{children}
			</div>
			{isVisible && (
				<div
					className={`absolute z-10 p-2 min-w-[300px] text-sm bg-white rounded-md ${positionClasses} box-shadow-md border border-gray-300`}
				>
					<div className="border-b border-b-gray-200 py-2">
						<FAIcon icon={icon} className="text-glazier-blue-500 mr-2" />
						{title}
					</div>
					{visibleValues.map((job) => (
						<div
							key={job.id}
							className={clsx('flex justify-between items-center py-2', {
								'border-b border-b-gray-200': job !== visibleValues[visibleValues.length - 1],
							})}
						>
							<div className="whitespace-pre-wrap">{job.label}</div>
						</div>
					))}
					{extraValues > 0 && <div className="mt-2 text-blue-500 text-center cursor-pointer">More {extraValues} Values...</div>}
				</div>
			)}
		</div>
	);
}
